import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DocumentGridCard } from './DocumentGridCard';
import type { RAGDocument } from '@/types/rag';

import { DocumentGridCardSkeleton } from './DocumentGridCardSkeleton';

interface KnowledgeGridProps {
    documents: RAGDocument[];
    selectedIds: Set<string>;
    onToggleSelect: (id: string, selected: boolean) => void;
    onView: (doc: RAGDocument) => void;
    onDelete: (id: string) => void;
    isLoading?: boolean;
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.05 }
    }
};

export const KnowledgeGrid: React.FC<KnowledgeGridProps> = ({
    documents,
    selectedIds,
    onToggleSelect,
    onView,
    onDelete,
    isLoading = false
}) => {
    if (isLoading) {
        return (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <DocumentGridCardSkeleton key={i} />
                ))}
            </div>
        );
    }

    if (documents.length === 0) {
        return null; // Parent will show upload zone
    }

    return (
        <motion.div
            {...({
                variants: containerVariants,
                initial: "hidden",
                animate: "visible"
            } as any)}
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
            role="grid"
        >
            <AnimatePresence mode="popLayout">
                {documents.map((doc, index) => (
                    <DocumentGridCard
                        key={doc.id}
                        doc={doc}
                        isSelected={selectedIds.has(doc.id)}
                        onToggleSelect={onToggleSelect}
                        onView={onView}
                        onDelete={onDelete}
                        index={index}
                    />
                ))}
            </AnimatePresence>
        </motion.div>
    );
};
